import { useState } from "react";
import { Send, CheckCircle2, Loader2, MessageCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { buildWhatsAppLink } from "@/lib/contact";
import { useT } from "@/lib/i18n";

const PROGRAM_KEYS = [
  { value: "Playgroup", key: "programs.playgroup" },
  { value: "Nursery", key: "programs.nursery" },
  { value: "LKG", key: "programs.lkg" },
  { value: "UKG", key: "programs.ukg" },
  { value: "Day Care", key: "programs.daycare" },
];

const initialState = {
  parent_name: "",
  phone: "",
  email: "",
  child_name: "",
  child_age: "",
  program: "",
  message: "",
};

const buildInquiryMessage = (f) => {
  const lines = [
    "Hello Little Ark Preschool! I just submitted an admission inquiry:",
    "",
    `Parent: ${f.parent_name}`,
    `Phone: ${f.phone}`,
  ];
  if (f.email) lines.push(`Email: ${f.email}`);
  if (f.child_name) lines.push(`Child: ${f.child_name}`);
  if (f.child_age) lines.push(`Age: ${f.child_age}`);
  if (f.program) lines.push(`Program: ${f.program}`);
  if (f.message) lines.push(`Note: ${f.message}`);
  lines.push("", "Please share the next steps to book a school tour. Thank you!");
  return lines.join("\n");
};

const InquiryForm = () => {
  const { t } = useT();
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [waLink, setWaLink] = useState(buildWhatsAppLink());

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.parent_name || !form.phone) {
      toast.error(t("inquiry.errRequired"));
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });
      await api.post("/inquiries", payload);
      const snapshot = { ...form };
      const link = buildWhatsAppLink(buildInquiryMessage(snapshot));
      setWaLink(link);
      setDone(true);
      setForm(initialState);
      toast.success(t("inquiry.thankToast"));
      const win = window.open(link, "_blank", "noopener,noreferrer");
      if (!win) {
        toast.message(t("inquiry.fallbackToast"));
      }
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error(t("inquiry.errRate"));
      } else {
        const msg =
          err?.response?.data?.detail ||
          t("inquiry.errGeneric");
        toast.error(typeof msg === "string" ? msg : t("inquiry.errGeneric"));
      }
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="inquiry-success"
        className="rounded-4xl bg-white p-8 md:p-12 shadow-ark-soft border border-sky-100 text-center"
      >
        <div className="mx-auto h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
          <CheckCircle2 className="h-9 w-9 text-green-600" strokeWidth={2.4} />
        </div>
        <h3 className="mt-5 font-display text-2xl md:text-3xl font-bold text-ark-ink">
          {t("inquiry.successTitle")}
        </h3>
        <p className="mt-3 text-slate-600 leading-relaxed">
          {t("inquiry.successBody")}
        </p>
        <a
          href={waLink}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="success-whatsapp-btn"
          className="mt-6 inline-flex items-center justify-center gap-2 h-14 px-7 rounded-full bg-green-500 hover:bg-green-600 text-white font-display font-semibold text-base shadow-lg shadow-green-600/30 transition"
        >
          <MessageCircle className="h-5 w-5" strokeWidth={2.5} />
          {t("inquiry.confirmWa")}
        </a>
        <div>
          <Button
            data-testid="submit-another-btn"
            onClick={() => setDone(false)}
            variant="ghost"
            className="mt-4 rounded-full text-ark-tealDeep hover:bg-teal-50"
          >
            {t("inquiry.submitAnother")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="inquiry-form"
      className="rounded-4xl bg-white p-6 md:p-8 shadow-ark-soft border border-sky-100 space-y-5"
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="parent_name" className="text-sm font-semibold text-slate-700">
            {t("inquiry.parentName")} *
          </Label>
          <Input
            id="parent_name"
            data-testid="inquiry-parent-name"
            value={form.parent_name}
            onChange={(e) => update("parent_name", e.target.value)}
            placeholder={t("inquiry.parentNamePh")}
            className="rounded-2xl h-12 border-slate-200 focus-visible:ring-ark-sky"
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone" className="text-sm font-semibold text-slate-700">
            {t("inquiry.phone")} *
          </Label>
          <Input
            id="phone"
            data-testid="inquiry-phone"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder={t("inquiry.phonePh")}
            className="rounded-2xl h-12 border-slate-200 focus-visible:ring-ark-sky"
            required
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-semibold text-slate-700">
            {t("inquiry.email")}
          </Label>
          <Input
            id="email"
            type="email"
            data-testid="inquiry-email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder={t("inquiry.emailPh")}
            className="rounded-2xl h-12 border-slate-200 focus-visible:ring-ark-sky"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="program" className="text-sm font-semibold text-slate-700">
            {t("inquiry.program")}
          </Label>
          <Select value={form.program} onValueChange={(v) => update("program", v)}>
            <SelectTrigger
              id="program"
              data-testid="inquiry-program"
              className="rounded-2xl h-12 border-slate-200 focus:ring-ark-sky"
            >
              <SelectValue placeholder={t("inquiry.programPh")} />
            </SelectTrigger>
            <SelectContent>
              {PROGRAM_KEYS.map((p) => (
                <SelectItem key={p.value} value={p.value} data-testid={`program-option-${p.value.toLowerCase().replace(/\s+/g, '-')}`}>
                  {t(p.key)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="child_name" className="text-sm font-semibold text-slate-700">
            {t("inquiry.childName")}
          </Label>
          <Input
            id="child_name"
            data-testid="inquiry-child-name"
            value={form.child_name}
            onChange={(e) => update("child_name", e.target.value)}
            placeholder={t("inquiry.childNamePh")}
            className="rounded-2xl h-12 border-slate-200 focus-visible:ring-ark-sky"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="child_age" className="text-sm font-semibold text-slate-700">
            {t("inquiry.childAge")}
          </Label>
          <Input
            id="child_age"
            data-testid="inquiry-child-age"
            value={form.child_age}
            onChange={(e) => update("child_age", e.target.value)}
            placeholder={t("inquiry.childAgePh")}
            className="rounded-2xl h-12 border-slate-200 focus-visible:ring-ark-sky"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="message" className="text-sm font-semibold text-slate-700">
          {t("inquiry.message")}
        </Label>
        <Textarea
          id="message"
          data-testid="inquiry-message"
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          placeholder={t("inquiry.messagePh")}
          rows={4}
          className="rounded-2xl border-slate-200 focus-visible:ring-ark-sky resize-none"
        />
      </div>

      <Button
        type="submit"
        disabled={loading}
        data-testid="inquiry-submit-btn"
        className="w-full h-14 rounded-full bg-gradient-to-r from-ark-sky to-ark-teal hover:from-ark-skyDeep hover:to-ark-tealDeep text-white text-base font-display font-semibold shadow-ark-sky"
      >
        {loading ? (
          <>
            <Loader2 className="h-5 w-5 mr-2 animate-spin" /> {t("inquiry.sending")}
          </>
        ) : (
          <>
            <Send className="h-5 w-5 mr-2" strokeWidth={2.4} /> {t("inquiry.send")}
          </>
        )}
      </Button>

      <p className="text-xs text-center text-slate-500">
        {t("inquiry.disclaimer")}
      </p>
    </form>
  );
};

export default InquiryForm;
